import { getPaneElements } from '../../util/chartPanes'
import { OiDataTypeBars, SdDataType } from '../../pages/home/plChart'
import { Box, Grid, ListItemIcon, ListItemText, MenuItem, Popover, useTheme } from '@mui/material'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import React, { memo, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { createPortal } from 'react-dom'
import PayoffChart, { ClickInfo } from './PayoffChart'
import {
    clearPayoffGraphMetaData,
    PAYOFF_CHART_META_KEY,
    payoffMetaFromSnapshot,
    setPayoffGraphMetaData
} from '../payoffGraph/payoffGraphMetaDataStore'
import { ColorType, IChartApi, IRange, Time } from 'lightweight-charts'
import { ChartLayoutOptions } from '../chartV2/chart.typings'
import { debounce } from 'throttle-debounce'
import PayoffMetaData from './PayoffMetaData'
import { TimeAxisLabelData } from '../chartV2/customSeries/axisLabels'
import { OptionOrderDataType, OrderDataPrices, OrderState, OrdersDataType, setHoveredOrderIndex, setOrderDataFunction } from '../../reducer/orders.reducer'
import { OrderStatus } from '../../proto/generated/order'
import OrderSlider, { greenFill, redFill } from '../../assets/icons/orderSlider'
import { arePlDataEqual, findNearestNumber, isOrderFieldEditable } from '../../util/tradepanel'
import { optionsTradesDataType, optionTradeDataType } from '../../pages/typings/options.typings'
import { Security } from '../../proto/generated/security'
import { v4 as uuid } from 'uuid'
import { getSecurityKey } from '../../util/security'
import { useSelector } from 'react-redux'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { VerticalLineData } from '../chartV2/customSeries/verticalLine/data'
import { useStyles } from './styles'
import PayoffLegends from './payoffLegends'
import { PaneLabelData } from '../chartV2/customSeries/PaneLabelsPrimitive'
import { useDispatch } from 'react-redux'
import { callColorCumulative, putColorCumulative } from '../multiStikeChart/styles'
import { NonInteractiveTooltip } from '../custom'
import PayoffSettingsDialog from './PayoffSettingsDialog'
import PayoffChartSettingsOverlay from './payoffChartSettingsOverlay'
import { selectPayoffPreferences } from '../../selectors/user'
import { createStringDateFromStamp } from '../../util/tradePage'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowUpIcon from '../../assets/icons/ThinArrowUpIcon'
import { deepClone } from '@mui/x-data-grid/utils/utils'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'

export type CustomProfitType = {
    chosenSpotPrice: number
    profit: number
}

export type PayoffDataType = OiDataTypeBars & { basketAnalyzeProfit?: number; basketAnalyzeExpectedProfit?: number }

export type PayoffWrapperProps = {
    margin?: { top: number; right: number; bottom: number; left: number }
    data: PayoffDataType[]
    spotPrice: number
    readOnly: boolean
    showBasketProfit: boolean
    breakEvenPoints: number[]
    handleAddOrder: (data: { type: 'buy' | 'sell', record: optionTradeDataType, callput: 'call' | 'put' }[]) => Promise<void>
    showPlGraph: boolean
    expandedRows: string[]
    orders: OrdersDataType[]
    orderState: OrderState
    sdData: SdDataType[]
    markerPositions: number[]
    handleStrikeChange: Function
    setMarkerPositions: (markerPositons: number[]) => void
    markerPositionsRef: React.RefObject<number[]>
    optionExpiries: optionsTradesDataType[]
    security: Security | undefined
    orderPrices: OrderDataPrices[]
    showSliders?: boolean
    historicalOptionChain: boolean
}

const chartContainerStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gridArea: 'content',
    position: 'relative'
}

const PayoffWrapper = (props: PayoffWrapperProps) => {
    const {
        data,
        spotPrice,
        orders,
        orderState,
        markerPositions,
        handleStrikeChange,
        breakEvenPoints,
        setMarkerPositions,
        markerPositionsRef,
        orderPrices,
        sdData,
        security,
        optionExpiries,
        showPlGraph,
        readOnly,
        handleAddOrder,
        showBasketProfit,
        showSliders = true,
        historicalOptionChain
    } = props
    const dispatch = useDispatch()
    const ws = useContext(WebSocketContext)
    const { width, height, ref } = useResizeDetector()
    const chart = useRef<PayoffChart>(undefined)
    const [container, setContainer] = useState<HTMLDivElement>()
    const prevData = useRef<OiDataTypeBars[]>(undefined)
    const underlyingToken = security ? getSecurityKey(security.exchange, security.code) : ''
    const optionsTradesData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const [loading, setLoading] = useState<boolean>(false)
    const [paneContainer, setPaneContainer] = useState<HTMLElement>()
    const [chartApi, setChartApi] = useState<IChartApi>()
    const [dragging, setDragging] = useState(false)
    const draggingRef = useRef(false)
    const [draggingOrderNumber, setDraggingOrderNumber] = useState<number>()
    const [timeRange, setTimeRange] = useState<IRange<Time> | null>(null)
    const [paneScrollEvent, setPaneScrollEvent] = useState<any>()
    const [scrollId, setScrollId] = useState<string>()
    const chartMountNodeRef = useRef<HTMLDivElement | null>(null)
    const [chartMountNode, setChartMountNode] = useState<HTMLDivElement | null>(null)
    const anchorEl: RefObject<HTMLDivElement | undefined> = useRef<HTMLDivElement>(undefined)
    const [record, setRecord] = useState<optionTradeDataType>()
    const [clickInfo, setClickInfo] = useState<ClickInfo>()
    const [dragOrderIndex, setDragOrderIndex] = useState<number>()
    const [openSettingsDialog, setOpenSettingsDialog] = useState(false)
    const { showOiBars = true, showSdLines = true } = useSelector(selectPayoffPreferences)
    const { classes } = useStyles()
    const isMouseInChartRef = useRef<boolean>(false)
    const maxZIndexRef = useRef(0)
    const [ordersZIndex, setOrdersZIndex] = useState<Record<number, number>>({})
    const mouseDownTimeRef = useRef<number | null>(null)
    const clickThreshold = 300
    const strikes = useMemo(() => {
        const st = new Set<number>()
        data.forEach((d) => {
            if (d.axisStrike && d.strike) {
                st.add(d.strike)
            }
        })
        return st
    }, [data])
    const [settingsButtonWidth, setSettingsButtonWidth] = useState<number>(0)
    const { allOrders, allOrdersPrices } = useCombineBasketAnalyzeAndLegOrders()

    useEffect(() => {
        const newZIndexObj: Record<number, number> = {}
        orders.forEach((order, i) => {
            if (!ordersZIndex[i]) {
                newZIndexObj[i] = 7 + i
            } else {
                newZIndexObj[i] = ordersZIndex[i]
            }
            if (newZIndexObj[i] > maxZIndexRef.current) {
                maxZIndexRef.current = newZIndexObj[i]
            }
        })
        setOrdersZIndex(newZIndexObj)
    }, [orders])

    const numberOfReadOnlyOrders = useMemo(() => {
        let i = 0
        while (i < orders.length && orders[i].readonly) {
            i++
        }
        return i
    }, [orders])


    const handleRef = useCallback((ref: HTMLDivElement) => {
        setContainer(ref)
    }, [])

    const renderChart = useMemo(() => container !== undefined && Boolean(data), [container, data])
    const theme = useTheme()

    const sliderOrders = useMemo(()=>{
        const copy: OrdersDataType[] = deepClone(orders)
        const readOnlyOrders = copy.filter(o=>o?.readonly)
        const editableOrders = copy.filter(o=>!o?.readonly)
        readOnlyOrders.forEach((o=>{
            const similarOrderIndex = editableOrders.findIndex(e=>e?.security?.code===o?.security?.code)
            if(similarOrderIndex!==-1 && editableOrders?.[similarOrderIndex]?.quantity === o?.quantity && editableOrders?.[similarOrderIndex]?.type !== o?.type && editableOrders?.[similarOrderIndex]?.visibility) {
                o.sqOffByOrderId = editableOrders[similarOrderIndex].uuid
                editableOrders[similarOrderIndex].sqOffWithOrderId = o.uuid
            }
        }))
        return [...readOnlyOrders, ...editableOrders]
    }, [orders])

    const uuidToIndexMap = useMemo(()=>{
        const map: Record<string, number> = {}
        orders.forEach((o, i)=>{
            map[o.uuid] = i
        })
        return map
    }, [orders])

    const axisLabelsData = useMemo(() => {
        const timeAxisLabels: TimeAxisLabelData[] = []
        orders.forEach((order, i) => {
            if (order.visibility) {
                const strike = (order.contractType === 'FUTURE' ? (orderPrices[i].price) : order.data.strikeCol.strike / (order.security?.priceMultiplier ?? 100))
                const time = Number(markerPositions?.[i] ?? strike)
                const lbData = {
                    time: time,
                    text: `${strike}${order.contractType !== 'FUTURE' ? order.callput === 'call' ? ' CE' : ' PE' : ''}`,
                    textColor: order.contractType === 'OPTION' ? order.callput === 'call' ? 'white' : 'black' : theme.palette.text.primary,
                    backColor: order.contractType === 'OPTION' ? order.callput === 'call' ? callColorCumulative : putColorCumulative : order.type === 'buy' ? greenFill : redFill
                }
                //pushing 1st time just to hide the background with paper color
                timeAxisLabels.push({
                    ...lbData,
                    textColor: 'transparent',
                    backColor: theme.palette.background.paper
                })
                //pushing for actual visual
                timeAxisLabels.push(lbData)
            }
        })
        return {
            timeAxisLabels: timeAxisLabels.sort((a, b) => a.time - b.time),
            priceAxisLabels: []
        }
    }, [orders, theme, markerPositions, orderPrices])

    const mergeSameTimes = useCallback((data: VerticalLineData[]) => {
        const groupedData = new Map<number, VerticalLineData[]>()
        data.forEach((item) => {
            if (!groupedData.has(item.time as number)) {
                groupedData.set(item.time as number, [])
            }
            groupedData.get(item.time as number)!.push(item)
        })

        // Process each group, mixing colors for items with the same time
        const result: VerticalLineData[] = []
        for (const [time, items] of groupedData) {
            const lastItem = items[items.length - 1]
            result.push({ ...lastItem })
        }

        return result
    }, [])

    const [paneLabelsData, verticalLinesData] = useMemo(() => {
        const paneLabels: PaneLabelData[] = []
        const newVerticalLineData: VerticalLineData[] = []
        if (showSdLines) {
            sdData.forEach((sd) => {
                paneLabels.push({
                    time: sd.time,
                    price: Number.NEGATIVE_INFINITY,
                    text: sd.label,
                    textHeight: 10,
                    textColor: '#88888850',
                    verticalAlign: 'bottom',
                    horizontalAlign: 'center',
                    backColor: theme.palette.background.paper
                })
                newVerticalLineData.push({
                    time: sd.time as Time,
                    sy: NaN,
                    ey: NaN,
                    opacity: NaN,
                    width: NaN,
                    customValues: {
                        color: '#88888850'
                    }
                })
            })
        }
        const sortedData = newVerticalLineData.sort((a, b) => Number(a.time) - Number(b.time))
        const distinctTimesData = mergeSameTimes(sortedData)
        return [{
            paneLabels: paneLabels
        }, distinctTimesData]
    }, [mergeSameTimes, sdData, showSdLines, theme.palette.background.paper])

    const handlePaneScroll = useCallback((e: any) => {
        setPaneScrollEvent(e)
    }, [])

    useEffect(() => {
        chart.current && chart.current.updateBreakEvenPoints(breakEvenPoints)
    }, [breakEvenPoints])

    useEffect(() => {
        if (!chart.current) return
        chart.current.updateShowBasketData(showBasketProfit)
        if (showBasketProfit) {
            chart.current.initializeBasketPLSerieses()
        } else {
            chart.current.clearBasketPLSerieses()
        }
        if (data.length) {
            chart.current.updateData(data)
        }
    }, [showBasketProfit, chartApi])

    useEffect(() => {
        return () => {
            chart.current && chart.current.destroy()
            clearPayoffGraphMetaData(PAYOFF_CHART_META_KEY)
        }
    }, [])

    const layoutOptions = useMemo(() => {
        return {
            localization: {
                timeFormatter: (time: any) => {
                    return time.toString()
                }
            },
            handleScale: {
                axisPressedMouseMove: {
                    price: true
                }
            },
            handleScroll: {
                pressedMouseMove: true,
                pressedVerticalMouseMove: false
            },
            timeScale: {
                tickMarkFormatter: (time: any) => {
                    if (strikes.has(time as number)) return time.toString()
                    return ''
                },
                borderColor: theme.palette.divider,
                fixLeftEdge: true,
                borderVisible: false,
                fixRightEdge: true,
                visible: !readOnly
            },
            layout: {
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper
                },
                fontFamily: `'Work Sans', sans-serif`,
                textColor: theme.palette.text.secondary
            },
            grid: {
                horzLines: {
                    color: '#88888815',
                    visible: false
                },
                vertLines: {
                    color: '#88888815',
                    visible: false
                }
            },
            rightPriceScale: {
                alignLabels: true,
                borderColor: theme.palette.divider,
                visible: !readOnly,
                autoScale: false,
                invertScale: false,
                borderVisible: false
            },
            leftPriceScale: {
                alignLabels: true,
                borderColor: theme.palette.divider,
                visible: showPlGraph && !readOnly,
                autoScale: true,
                invertScale: false,
                borderVisible: false
            },
            width: width,
            height: height
        } as ChartLayoutOptions
    }, [theme, height, width, showPlGraph, readOnly, strikes])

    const updateData = useCallback((data: PayoffDataType[], showPlGraph: boolean, readOnly: boolean) => {
        if (chart.current && data.length && JSON.stringify(prevData.current) !== JSON.stringify(data)) {
            chart.current.updateShowPlChart(showPlGraph)
            const visibleTimeRange = chartApi?.timeScale().getVisibleRange()
            const needsPriceScalesReset = (prevData.current?.length && !readOnly && !arePlDataEqual(data, prevData.current ?? []))
            chart.current && chart.current.updateData(data)

            if (needsPriceScalesReset) {
                // chart.curtarget-day-containerrent && chart.current.fitTimeScale()
                setTimeout(() => {
                    chartApi &&
                        chartApi.applyOptions({
                            leftPriceScale: {
                                borderVisible: false,
                                autoScale: true
                            },
                            rightPriceScale: {
                                borderVisible: false,
                                autoScale: false
                            }
                        })

                    setTimeout(() => {
                        chartApi &&
                            chartApi.applyOptions({
                                leftPriceScale: {
                                    borderVisible: false,
                                    autoScale: true
                                },
                                rightPriceScale: {
                                    borderVisible: false,
                                    autoScale: false
                                }
                            })
                        visibleTimeRange && chartApi && chartApi.timeScale().setVisibleRange(visibleTimeRange)
                    }, 0)
                }, 0)
            }
        }
        if (data.length) {
            prevData.current = [...data]
        }

    }, [chartApi])

    const updateDataDebouncer = useMemo(() => (debounce(150, updateData)), [updateData])

    useEffect(() => {
        const needsPriceScalesReset = (prevData.current?.length && !readOnly && !arePlDataEqual(data, prevData.current ?? []))
        if (needsPriceScalesReset) {
            updateDataDebouncer(data, showPlGraph, readOnly)
        } else {
            updateData(data, showPlGraph, readOnly)
        }
    }, [data])

    const onSeriesGroupClickedCallback = useCallback((info: ClickInfo) => {
        let expiry = orders.filter(o => o.visibility).map((order) => order.security?.expiry?.expiry)?.sort()?.[0]
        if(!expiry && optionsTradesData && (optionsTradesData??[]).length>0) {
            expiry = optionsTradesData[0].expiration.expiry?.expiry
        }
        const r = (optionsTradesData ?? []).find((otd) => otd.expiration.expiry?.expiry === expiry)?.trades?.find((tr) => tr.strikeCol.strike / (security?.priceMultiplier ?? 100) === info.strike)
        if (r) {
            JSON.stringify(r) !== JSON.stringify(record) && setRecord(r)
            JSON.stringify(clickInfo) !== JSON.stringify(info) && setClickInfo(info)
        }
    }, [clickInfo, optionsTradesData, orders, record, security?.priceMultiplier])

    useEffect(() => {
        if (!container || chart.current || !ws || !renderChart || !data.length) return

        chart.current = new PayoffChart({
            container,
            paneContainer,
            layoutOptions,
            showPlGraph: showPlGraph,
            ws,
            data: data ?? [],
            spotPrice: spotPrice,
            breakEvenPoints: breakEvenPoints,
            initialOrderStrikes: orders.map((order, i) => (order.contractType === 'FUTURE' ? Number(orderPrices[i].price) : order.data.strikeCol.strike / (order.security?.priceMultiplier ?? 100))),
            showBasketData: showBasketProfit,
            readOnly: readOnly,
            axisLabelsData: axisLabelsData,
            paneLabelsData: paneLabelsData,
            showOiBars: showOiBars,
            onDataUpdate: () => {
                setPayoffGraphMetaData(PAYOFF_CHART_META_KEY, payoffMetaFromSnapshot(chart.current?.snapshotData))
            },
            onCrosshairMove: (event: any) => {
            },
            onTimeRangeChange: (timeRange: IRange<Time> | null) => {
            },
            onVisibleTimeRangeChange: (timeRange: IRange<Time> | null) => {
                setTimeRange(timeRange)
            },
            onScroll: () => {
                if (isMouseInChartRef.current) {
                    setScrollId(uuid())
                }
            },
            onGroupSeriesHovered: (hovered: boolean) => {
                if (chartMountNodeRef.current) {
                    chartMountNodeRef.current.style.cursor = hovered ? 'pointer' : ''
                }
            },
            onGroupSeriesClick: onSeriesGroupClickedCallback
        })
        setChartApi(chart.current.chartApi)
        setSettingsButtonWidth(chart.current?.chartApi?.priceScale('right')?.width() ?? 0)
        //@ts-ignore
        window.payoffChart = chart.current
    }, [renderChart, container, data])

    useEffect(()=>{
        if(chart.current) {
            chart.current.spotPrice = spotPrice
            chart.current.onCrosshairMove(null)
        }
    }, [spotPrice])

    useEffect(()=>{
        if(chart.current) {
            chart.current.updateOnGroupSeriesClickedCallback(onSeriesGroupClickedCallback)
        }
    }, [onSeriesGroupClickedCallback])

    useEffect(() => {
        chart.current && chart.current.updateAxisLabelData(axisLabelsData)
    }, [axisLabelsData])

    useEffect(() => {
        if(chartApi && chart.current) {
            chart.current.updatePaneLabelData(paneLabelsData, verticalLinesData)
        }
    }, [paneLabelsData, verticalLinesData, chartApi])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateGroupSeriesVisibility(showOiBars)
            setSettingsButtonWidth(chart.current?.chartApi?.priceScale('right')?.width() ?? 0)
        }
    }, [readOnly, showOiBars, theme.palette.divider])

    useEffect(() => {
        if (chart.current) {
            chart.current.chartApi.applyOptions(layoutOptions)
        }
    }, [layoutOptions])

    useEffect(() => {
        if (!chartApi) return
        const paneElements = getPaneElements(chartApi)
        paneElements.length && setPaneContainer(paneElements[0])
        if(chart.current) chart.current.updatePaneContainer(paneElements[0])
    }, [chartApi])

    useEffect(() => {
        if (!paneContainer) return
        paneContainer.addEventListener('scroll', handlePaneScroll)

        return () => {
            paneContainer.removeEventListener('scroll', handlePaneScroll)
        }
    }, [paneContainer, handlePaneScroll])

    const handleDragStart = useCallback((i: number, event: any) => {
        mouseDownTimeRef.current = Date.now();
        if (readOnly || sliderOrders[i].contractType !== 'OPTION' || sliderOrders[i].sqOffByOrderId) return
        // event.target.parentNode.appendChild(event.target)
        if (sliderOrders[i].readonly || isOrderFieldEditable('strike', sliderOrders[i]?.orderDetails?.status ?? OrderStatus.ORDER_STATUS_UNKNOWN, orderState)) {
            setDragging(true)
            draggingRef.current = true
            dispatch(setHoveredOrderIndex([i - numberOfReadOnlyOrders]))
            setDragOrderIndex(i)
            setDraggingOrderNumber(i)
        }
    }, [readOnly, sliderOrders, orderState, dispatch, numberOfReadOnlyOrders])

    const handleMouseUp = useCallback((i: number) => {
        if (mouseDownTimeRef.current !== null) {
            const elapsedTime = Date.now() - mouseDownTimeRef.current;
            if (elapsedTime <= clickThreshold) {
                const getIndexes = (arr: number[], target: number) => arr.reduce((acc: number[], num, index) => (num === target ? [...acc, index] : acc), []);
                const indices = getIndexes(markerPositions, markerPositions[i])
                if (indices.length > 1) {
                    const minZIndex = indices.reduce((min, num) =>
                        ordersZIndex[num] < ordersZIndex[min] ? num : min
                        , 0)
                    const newObj = { ...ordersZIndex }
                    newObj[minZIndex] = maxZIndexRef.current + 1
                    maxZIndexRef.current = newObj[minZIndex]
                    setOrdersZIndex(newObj)
                }
            }
        }
    }, [markerPositions, ordersZIndex])

    const getLocalX = useCallback((clientX: number) => {
        if (clientX < paneContainer!.getBoundingClientRect().left) return 0
        if (clientX >= paneContainer!.getBoundingClientRect().right) return paneContainer!.getBoundingClientRect().width - 1
        return clientX - paneContainer!.getBoundingClientRect().left
    }, [paneContainer])

    const handleDrag = useCallback((event: any, i: number) => {
        event.stopPropagation()
        if (!(paneContainer && chart.current && chart.current.chartApi)) return
        if (dragging && draggingOrderNumber === i) {
            const { clientX } = event
            const localX = getLocalX(clientX)
            if (Number.isFinite(localX)) {
                const strike = chart.current.coordinateToTime(localX)
                if (strike && draggingRef.current) {
                    const newPositions = [...markerPositionsRef.current]
                    newPositions[i] = strike ?? 0
                    setMarkerPositions(newPositions)
                }
            }
        }
    }, [paneContainer, dragging, draggingOrderNumber, getLocalX, markerPositionsRef, setMarkerPositions])

    const handleDragEnd = useCallback((event: any, i: number) => {
        if (dragging && orders[i].contractType === 'OPTION') {
            draggingRef.current = false
            const { clientX } = event
            const localX = getLocalX(clientX)
            if (Number.isFinite(localX)) {
                const nearestDataPoint = chart.current?.getNearestDataPoint(localX)
                if (nearestDataPoint) {
                    const expirydata = optionExpiries.find((o) => o.expiration.expiry?.expiry === orders[i].security?.expiry?.expiry)
                    const originalStrikes = expirydata?.trades.map((t) => t.strikeCol.strike / (security?.priceMultiplier ?? 100))
                    if (originalStrikes) {
                        const nearestOriginalDataPoint = findNearestNumber(originalStrikes, nearestDataPoint.strike ?? 0)
                        if (nearestOriginalDataPoint) {
                            const existingOrder = sliderOrders.find(o=>o.sqOffWithOrderId && (o.security.strikePrice/(o.security?.priceMultiplier??100))===nearestOriginalDataPoint)
                            if(existingOrder && existingOrder.contractType==='OPTION' && orders[i].type !== existingOrder.type && orders[i].security?.expiry?.expiry === existingOrder.security?.expiry?.expiry && orders[i].callput === existingOrder.callput && orders[i].quantity === existingOrder.quantity) {
                                const index1 = allOrders.findIndex(o=>o.uuid===orders[i].uuid)
                                const index2 = allOrders.findIndex(o=>o.uuid===existingOrder.uuid)
                                if(index1!==index2) {
                                    const newOrders = [...allOrders]
                                    const newOrdersPrices = [...allOrdersPrices]
                                    newOrders.splice(Math.max(index1, index2), 1)
                                    newOrders.splice(Math.min(index1, index2), 1)
                                    newOrdersPrices.splice(Math.max(index1, index2), 1)
                                    newOrdersPrices.splice(Math.min(index1, index2), 1)
                                    setOrderDataFunction({ orders: newOrders, orderPrices: newOrdersPrices })
                                    const newPositions = [...markerPositionsRef.current]
                                    const indexSecond = orders.findIndex(o=>o.uuid===existingOrder.uuid)
                                    newPositions.splice(Math.max(i, indexSecond), 1)
                                    newPositions.splice(Math.min(i, indexSecond), 1)
                                    return
                                }
                            }
                            if(orders[i]?.readonly) {
                                if(orders[i].contractType !== 'OPTION') return
                                const prevStrikeData = expirydata.trades.find((t) => t.strikeCol.strike / (security?.priceMultiplier ?? 100) === (orders[i].data as optionTradeDataType).strikeCol.strike / (security?.priceMultiplier ?? 100))
                                const strikeData = expirydata?.trades.find((t) => t.strikeCol.strike / (security?.priceMultiplier ?? 100) === nearestOriginalDataPoint)
                                if(strikeData && prevStrikeData && prevStrikeData.strikeCol.strike !== strikeData.strikeCol.strike) {
                                    const orderData = orders[i] as OptionOrderDataType
                                    handleAddOrder([
                                        { type: orderData.type === 'buy' ? 'sell' : 'buy', record: prevStrikeData, callput: orderData.callput},
                                        { type: orderData.type, record: strikeData, callput: orderData.callput}
                                    ])
                                } else {
                                    const newPositions = [...markerPositionsRef.current]
                                    newPositions[i] = prevStrikeData.strikeCol.strike / (security?.priceMultiplier ?? 100)
                                    setMarkerPositions(newPositions)
                                }
                            } else {
                                const newStrike = nearestOriginalDataPoint * (security?.priceMultiplier ?? 100)
                                if (newStrike !== (orders[i] as OptionOrderDataType).data.strikeCol.strike) handleStrikeChange(i, newStrike, orders, orderPrices)
                                else {
                                    const newPositions = [...markerPositionsRef.current]
                                    newPositions[i] = newStrike / (security?.priceMultiplier ?? 100)
                                    setMarkerPositions(newPositions)
                                }
                            }
                        }
                    }
                }
            }
        }
        setDragging(false)
        setDragOrderIndex(undefined)
        dispatch(setHoveredOrderIndex(undefined))
        setDraggingOrderNumber(undefined)
    }, [dragging, orders, dispatch, getLocalX, optionExpiries, security?.priceMultiplier, handleStrikeChange, orderPrices, markerPositionsRef, setMarkerPositions, handleAddOrder, allOrders, allOrdersPrices, sliderOrders])

    const handleTooltipClose = useCallback((e: any) => {
        setRecord(undefined)
        setClickInfo(undefined)
    }, [])

    const coordinatesRef = useRef<number[]>([])
    const coordinates = useMemo(() => {
        const newCoordinates = markerPositions.map((mp) => {
            if (
                chart.current &&
                paneContainer &&
                container &&
                (chart.current.strikeToCoordinate(mp) < 0 || chart.current.strikeToCoordinate(mp) >= paneContainer.getBoundingClientRect().right - paneContainer.getBoundingClientRect().left)
            )
                return -1000
            return chart.current && paneContainer && container ? chart.current.strikeToCoordinate(mp) + (paneContainer.getBoundingClientRect().left - container.getBoundingClientRect().left) : -1000
        })
        if (JSON.stringify(coordinatesRef.current) !== JSON.stringify(newCoordinates)) {
            coordinatesRef.current = newCoordinates
        }
        return coordinatesRef.current
    }, [markerPositions, renderChart, container, paneContainer, timeRange, paneScrollEvent, width, scrollId])

    useEffect(() => {
        if (!(chart.current && paneContainer && container && markerPositions.length && coordinates.length)) return
        if (readOnly) return
        if (!chart.current.orderLines) {
            chart.current.createOrderLines()
        }
        chart.current.updateOrderLinesData(orders, markerPositions, coordinates, dragOrderIndex)
    }, [orders, markerPositions, coordinates, paneContainer, container, readOnly])

    const handleBuySellButtonClick = useCallback((type: 'buy' | 'sell', callput: 'call' | 'put') => {
        if (Boolean(record)) {
            setLoading(true)
            handleAddOrder([{type, record: record!, callput}]).finally(() => {
                handleTooltipClose({})
                setLoading(false)
            })
        }
    }, [record, handleAddOrder, handleTooltipClose])

    const strikerTooltip = useMemo(() => {
        return !readOnly && record && clickInfo && paneContainer
            ? createPortal(
                <>
                    <Popover
                        open={Boolean(anchorEl?.current) && Boolean(record) && Boolean(clickInfo)}
                        anchorEl={anchorEl?.current}
                        onClose={handleTooltipClose}
                        anchorOrigin={{
                            vertical: 'center',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'center',
                            horizontal: 'left'
                        }} 
                    >
                        <MenuItem onClick={() => handleBuySellButtonClick('buy', 'call')}>
                            <ListItemIcon>
                                <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                            </ListItemIcon>
                            <ListItemText>
                                Buy {`${createStringDateFromStamp(record.callOption?.security?.expiry?.expiry ?? 0)} ${record.strikeCol.strike / (record.callOption?.security?.priceMultiplier ?? 100)} CE`}
                            </ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleBuySellButtonClick('sell', 'call')}>
                            <ListItemIcon>
                                <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/>
                            </ListItemIcon>
                            <ListItemText>
                                Sell {`${createStringDateFromStamp(record.callOption?.security?.expiry?.expiry ?? 0)} ${record.strikeCol.strike / (record.callOption?.security?.priceMultiplier ?? 100)} CE`}
                            </ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleBuySellButtonClick('buy', 'put')}>
                            <ListItemIcon>
                                <ThinArrowUpIcon sx={{fontSize: '1.5rem !important'}}/>
                            </ListItemIcon>
                            <ListItemText>
                                Buy {`${createStringDateFromStamp(record.putOption?.security?.expiry?.expiry ?? 0)} ${record.strikeCol.strike / (record.putOption?.security?.priceMultiplier ?? 100)} PE`}
                            </ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleBuySellButtonClick('sell', 'put')}>
                            <ListItemIcon>
                                <ThinArrowDownIcon sx={{fontSize: '1.5rem !important'}}/>
                            </ListItemIcon>
                            <ListItemText>
                                Sell {`${createStringDateFromStamp(record.putOption?.security?.expiry?.expiry ?? 0)} ${record.strikeCol.strike / (record.putOption?.security?.priceMultiplier ?? 100)} PE`}
                            </ListItemText>
                        </MenuItem>
                        {/* <StrikeTooltip
                            record={record!}
                            roundStrike={false}
                            partial={clickInfo.callPut}
                            keyValueType={true}
                            showBuySellButtons={true}
                            onBuySellButtonClick={handleBuySellButtonClick}
                            key={'payoff-striker-tooltip'}
                        /> */}
                    </Popover>
                </>,
                paneContainer
            )
            : null
    }, [readOnly, record, clickInfo, paneContainer, handleTooltipClose, handleBuySellButtonClick])

    useEffect(() => {
        const handleMouseMoveInSliderBox = (e: any) => {
            if (dragging) handleDrag(e, draggingOrderNumber ?? 0)
        }
        window.addEventListener('mousemove', handleMouseMoveInSliderBox)
        return () => {
            window.removeEventListener('mousemove', handleMouseMoveInSliderBox)
        }
    }, [dragging, draggingOrderNumber, handleDrag])

    useEffect(() => {
        const handleMouseLeaveOrUpInSliderBox = (e: any) => {
            if (dragging) handleDragEnd(e, draggingOrderNumber ?? 0)
        }
        window.addEventListener('mouseup', handleMouseLeaveOrUpInSliderBox)
        return () => {
            window.removeEventListener('mouseup', handleMouseLeaveOrUpInSliderBox)
        }
    }, [dragging, draggingOrderNumber, handleDragEnd])

    const nonEmptyOrders = useMemo(() => (orders && orders.length > 0), [orders])

    const handleMouseInChart = useCallback(() => {
        isMouseInChartRef.current = true
    }, [])

    const handleMouseOutChart = useCallback(() => {
        isMouseInChartRef.current = false
    }, [])

    const handleOpenSettings = useCallback(() => setOpenSettingsDialog(true), [])
    const handleCloseSettings = useCallback(() => setOpenSettingsDialog(false), [])

    // code for actual jsx tree
    const footer = useMemo(() => {
        return (showSliders && !readOnly) ? (
            <Box
                sx={{
                    height: orders.length > 0 ? '70px' : '32px',
                    width: '100%',
                    position: 'relative',
                    backgroundColor: (theme) => theme.palette.background.paper,
                    gridArea: 'footer'
                }}
            >
                {sliderOrders?.map((order, i) => {
                    return (order.visibility && !order?.sqOffWithOrderId) ? (
                        <span
                            key={`${i}-${order.uuid}-slider`}
                            onMouseDown={handleDragStart.bind(null, i)}
                            onMouseUp={() => handleMouseUp(i)}
                            style={{
                                position: 'absolute',
                                left: coordinates[i],
                                zIndex: ordersZIndex[i],
                                opacity: order.readonly && (!Boolean(order.sqOffByOrderId)) ? 0.5 : 1,
                                top: '-5px',
                                transform: 'translateX(-50%)',
                                cursor: order.contractType === 'OPTION' && isOrderFieldEditable('strike', orders[i]?.orderDetails?.status ?? OrderStatus.ORDER_STATUS_UNKNOWN, orderState) && !order.sqOffByOrderId
                                    ? 'grab'
                                    : 'not-allowed'
                            }}
                            onMouseEnter={() => {
                                if (dragOrderIndex === undefined) dispatch(setHoveredOrderIndex(order.sqOffByOrderId ? [i - numberOfReadOnlyOrders, uuidToIndexMap[order.sqOffByOrderId] - numberOfReadOnlyOrders] : [i - numberOfReadOnlyOrders]))
                            }} onMouseLeave={() => {
                                if (dragOrderIndex === undefined) dispatch(setHoveredOrderIndex(undefined))
                            }}
                        >
                            <Box id={`drag-adjustment-${i}`}>
                                <OrderSlider
                                    fillColor={order.type === 'buy' ? theme.palette.buy.main : theme.palette.sell.main}
                                    order={order}
                                    i={i}
                                    numberOfReadOnlyOrders={numberOfReadOnlyOrders}
                                    squaredOff={Boolean(order.sqOffByOrderId)}
                                    position={order?.readonly}
                                />
                            </Box>
                        </span>
                    ) : null
                })}
                <Grid container item alignItems={'center'} justifyContent={'space-between'}
                    sx={{ px: '10px', boxSizing: 'border-box', py: 1, position: 'absolute', top: 0 }}>
                    {/* {nonEmptyOrders && <PayoffLegends />} */}
                    <PayoffLegends />
                </Grid>
            </Box>
        ) : null
    }, [readOnly, sliderOrders, orders, handleDragStart, coordinates, ordersZIndex, orderState, numberOfReadOnlyOrders, handleMouseUp, dragOrderIndex, dispatch, showSliders])

    const payoffMetaData = useMemo(() => {
        return readOnly ? null : <PayoffMetaData paneContainer={paneContainer} historicalOptionChain={historicalOptionChain} />
    }, [readOnly, paneContainer, historicalOptionChain])

    const anchorElement = useMemo(() => {
        return (<Box
            ref={anchorEl}
            sx={{
                position: 'absolute',
                left: (paneContainer?.getBoundingClientRect()?.left ?? 0) - (ref.current?.getBoundingClientRect()?.left ?? 0) - (clickInfo?.leftScaleWidth ?? 0) + (clickInfo?.x ?? 0) + 90,
                top: clickInfo?.y
            }}
        ></Box>)
    }, [clickInfo?.leftScaleWidth, clickInfo?.x, clickInfo?.y, paneContainer, ref])

    const setChartContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            ref(node)
            chartMountNodeRef.current = node
            setChartMountNode((prev) => (prev === node ? prev : node))
        },
        [ref]
    )

    const chartContainer = useMemo(() => {
        return (
            <div style={chartContainerStyle} ref={setChartContainerRef} onScroll={handlePaneScroll}>
                <Box sx={{ position: 'relative', height: '100%', width: '100%', flex: 1 }} ref={handleRef}
                    onMouseEnter={handleMouseInChart} onMouseLeave={handleMouseOutChart} />
                {payoffMetaData}
                {anchorElement}
                {strikerTooltip}
            </div>
        )
    }, [setChartContainerRef, handlePaneScroll, handleRef, handleMouseInChart, handleMouseOutChart, payoffMetaData, anchorElement, strikerTooltip])

    const jsxTree = useMemo(() => {
        return (
            <Box
                className={`payoff-chart-container font-feature-num ${loading || !data.length ? `${classes.blur}` : ''} ${classes.container}`}
                key={'payoff-chart-container'}
                sx={{
                    padding: 0,
                    borderRadius: 1,
                    border: 'none',
                }}
            >
                {chartContainer}
                <PayoffSettingsDialog open={openSettingsDialog} onClose={handleCloseSettings} />
                {footer}
            </Box>
        )
    }, [loading, data.length, classes.blur, classes.container, chartContainer, openSettingsDialog, handleCloseSettings, footer])

    return (
        <>
            {jsxTree}
            <PayoffChartSettingsOverlay
                container={chartMountNode}
                readOnly={readOnly}
                width={settingsButtonWidth}
                onOpen={handleOpenSettings}
            />
        </>
    )
}

export default memo(PayoffWrapper)